import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import MuiChip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useLanguage } from "../contexts/LanguageContext";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import EdgesensorHighRoundedIcon from "@mui/icons-material/EdgesensorHighRounded";
import ViewQuiltRoundedIcon from "@mui/icons-material/ViewQuiltRounded";

const Chip = styled(MuiChip)(({ theme }) => ({
  borderColor: theme.palette.primary.light,
}));

export default function Features() {
  const { translations } = useLanguage();
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);

  const items = React.useMemo(
    () => [
      {
        icon: <ViewQuiltRoundedIcon />,
        title: translations.features_section.dashboard_title,
        description: translations.features_section.dashboard_description,
        imageLight: `url("https://rewardiou.com/images/Dashboard.png")`,
      },
      {
        icon: <EdgesensorHighRoundedIcon />,
        title: translations.features_section.mobile_title,
        description: translations.features_section.mobile_description,
        imageLight: `url("https://rewardiou.com/images/Mobile.png")`,
      },
      {
        icon: <DevicesRoundedIcon />,
        title: translations.features_section.platforms_title,
        description: translations.features_section.platforms_description,
        imageLight: `url("https://rewardiou.com/images/Mobile_Desktop.png")`,
      },
    ],
    [translations]
  );

  const handleItemClick = (index: number) => {
    setSelectedItemIndex(index);
  };

  const selectedFeature = items[selectedItemIndex];

  return (
    <Container id="features" sx={{ py: { xs: 8, sm: 16 } }}>
      <Box sx={{ width: { sm: "100%", md: "60%" }, mb: 4 }}>
        <Typography component="h2" variant="h4" gutterBottom sx={{ color: "text.primary" }}>
          {translations.features_section.features_description}
        </Typography>
      </Box>

      {/* Main Layout */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row-reverse" },
          gap: 4,
          alignItems: "stretch",
        }}
      >
        {/* Sidebar Buttons - with fixed height for each button */}
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: "column", 
            gap: 2,
            width: { xs: "100%", md: "250px" },
            flexShrink: 0 
          }}
        >
          {items.map(({ icon, title, description }, index) => (
            <Box
              key={index}
              component={Button}
              onClick={() => handleItemClick(index)}
              sx={[
                (theme) => ({
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "left",
                  height: "140px", // Fixed height for all buttons
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  overflow: "hidden" // Hide overflow if text is too long
                }),
                selectedItemIndex === index && { backgroundColor: "action.selected" },
              ]}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                {icon}
                <Typography variant="h6" noWrap>{title}</Typography>
                <Typography 
                  variant="body2"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Image Display - with fixed dimensions */}
        <Box
          sx={{
            flexGrow: 1,
            width: { xs: "100%", md: "calc(100% - 250px - 32px)" }, // Accounting for sidebar width and gap
          }}
        >
          <Card 
            variant="outlined" 
            sx={{ 
              width: "100%",
              height: "100%", 
              minHeight: { xs: "300px", md: "440px" }, // Fixed minimum height
              display: "flex"
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundImage: selectedFeature.imageLight,
                backgroundRepeat: "no-repeat",
              }}
            />
          </Card>
        </Box>
      </Box>
    </Container>
  );
}